/**
 * Socket hook cho phòng RPS p2p (member tự tạo phòng đấu 1v1).
 * Đăng ký listener cho mọi sự kiện server:rps_room_* và forward vào rpsRoomStore.
 */
import { useEffect, useCallback } from 'react';
import { getSocket } from './client';
import { SERVER_EVENTS, CLIENT_EVENTS } from './events';
import { rpsRoomStore } from '@/store/rpsRoomStore';
import { sessionStore } from '@/store/sessionStore';

export function useRpsRoomSocket(roomId: string | null, onError?: (code: string, message: string) => void) {
  useEffect(() => {
    if (!roomId) return;
    const token = sessionStore.getState().token;
    if (!token) return;

    const socket = getSocket(token);

    const onServerError = (raw: any) => {
      onError?.(raw?.code, raw?.message);
    };

    const onJoined = (raw: any) => {
      rpsRoomStore.getState().setJoined({
        room_id: raw.room_id,
        name: raw.name,
        status: raw.status,
        role: raw.role,
        my_employee_id: raw.my_employee_id,
        max_games: raw.max_games,
        time_limit_sec: raw.time_limit_sec,
        player1: raw.player1,
        player2: raw.player2,
        ready_ids: raw.ready_ids ?? [],
        spectator_count: raw.spectator_count ?? 0,
      });
    };

    const onStateUpdated = (raw: any) => {
      rpsRoomStore.getState().setStateUpdated({
        player1: raw.player1,
        player2: raw.player2,
        spectator_count: raw.spectator_count,
      });
    };

    const onPlayer2Left = () => {
      rpsRoomStore.getState().setStateUpdated({
        player1: rpsRoomStore.getState().player1,
        player2: null,
        spectator_count: rpsRoomStore.getState().spectatorCount,
      });
    };

    const onReadyUpdated = (raw: any) => {
      rpsRoomStore.getState().setReadyUpdated({ ready_ids: raw.ready_ids ?? [] });
    };

    const onMatchStarted = (raw: any) => {
      rpsRoomStore.getState().setMatchStarted({
        max_games: raw.max_games,
        time_limit_sec: raw.time_limit_sec,
        wins_needed: raw.wins_needed,
      });
    };

    const onGameStarted = (raw: any) => {
      rpsRoomStore.getState().setGameStarted({
        game_number: raw.game_number,
        started_at: raw.started_at,
        timer_sec: raw.timer_sec,
        player1_games_won: raw.player1_games_won,
        player2_games_won: raw.player2_games_won,
      });
    };

    const onPlayerChosen = (raw: any) => {
      rpsRoomStore.getState().setPlayerChosen({ employee_id: raw.employee_id });
    };

    const onGameRevealed = (raw: any) => {
      rpsRoomStore.getState().setGameRevealed({
        game_number: raw.game_number,
        player1_choice: raw.player1_choice,
        player2_choice: raw.player2_choice,
        result: raw.result,
        is_random_tiebreak: raw.is_random_tiebreak,
      });
    };

    const onMatchEnded = (raw: any) => {
      rpsRoomStore.getState().setMatchEnded({
        winner_id: raw.winner_id,
        player1_games_won: raw.player1_games_won,
        player2_games_won: raw.player2_games_won,
        games_played: raw.games_played,
        reason: raw.reason,
      });
    };

    const onClosed = (raw: any) => {
      rpsRoomStore.getState().setRoomClosed({ reason: raw.reason });
    };

    const onChatMessage = (raw: any) => {
      rpsRoomStore.getState().addChatMessage({
        sender_id: raw.sender_id,
        sender_name: raw.sender_name,
        sender_avatar: raw.sender_avatar,
        is_player: raw.is_player,
        message: raw.message,
        sent_at: raw.sent_at,
      });
    };

    socket.on(SERVER_EVENTS.RPS_ROOM_JOINED, onJoined);
    socket.on(SERVER_EVENTS.RPS_ROOM_STATE_UPDATED, onStateUpdated);
    socket.on(SERVER_EVENTS.RPS_ROOM_PLAYER2_LEFT, onPlayer2Left);
    socket.on(SERVER_EVENTS.RPS_ROOM_READY_UPDATED, onReadyUpdated);
    socket.on(SERVER_EVENTS.RPS_ROOM_MATCH_STARTED, onMatchStarted);
    socket.on(SERVER_EVENTS.RPS_ROOM_GAME_STARTED, onGameStarted);
    socket.on(SERVER_EVENTS.RPS_ROOM_PLAYER_CHOSEN, onPlayerChosen);
    socket.on(SERVER_EVENTS.RPS_ROOM_GAME_REVEALED, onGameRevealed);
    socket.on(SERVER_EVENTS.RPS_ROOM_MATCH_ENDED, onMatchEnded);
    socket.on(SERVER_EVENTS.RPS_ROOM_CLOSED, onClosed);
    socket.on(SERVER_EVENTS.RPS_ROOM_CHAT_MESSAGE, onChatMessage);
    socket.on(SERVER_EVENTS.ERROR, onServerError);

    return () => {
      socket.off(SERVER_EVENTS.RPS_ROOM_JOINED, onJoined);
      socket.off(SERVER_EVENTS.RPS_ROOM_STATE_UPDATED, onStateUpdated);
      socket.off(SERVER_EVENTS.RPS_ROOM_PLAYER2_LEFT, onPlayer2Left);
      socket.off(SERVER_EVENTS.RPS_ROOM_READY_UPDATED, onReadyUpdated);
      socket.off(SERVER_EVENTS.RPS_ROOM_MATCH_STARTED, onMatchStarted);
      socket.off(SERVER_EVENTS.RPS_ROOM_GAME_STARTED, onGameStarted);
      socket.off(SERVER_EVENTS.RPS_ROOM_PLAYER_CHOSEN, onPlayerChosen);
      socket.off(SERVER_EVENTS.RPS_ROOM_GAME_REVEALED, onGameRevealed);
      socket.off(SERVER_EVENTS.RPS_ROOM_MATCH_ENDED, onMatchEnded);
      socket.off(SERVER_EVENTS.RPS_ROOM_CLOSED, onClosed);
      socket.off(SERVER_EVENTS.RPS_ROOM_CHAT_MESSAGE, onChatMessage);
      socket.off(SERVER_EVENTS.ERROR, onServerError);
    };
  }, [roomId, onError]);
}

/** Tham gia phòng (player nếu nhập đúng mật khẩu và còn slot, ngược lại spectator) */
export function useRpsRoomJoin() {
  return useCallback((roomId: string, password?: string, spectateOnly?: boolean) => {
    const token = sessionStore.getState().token;
    if (!token) return;
    const socket = getSocket(token);
    socket.emit(CLIENT_EVENTS.RPS_ROOM_JOIN, { room_id: roomId, password, spectate_only: spectateOnly });
  }, []);
}

/** Rời phòng */
export function useRpsRoomLeave() {
  return useCallback((roomId: string) => {
    const token = sessionStore.getState().token;
    if (!token) return;
    const socket = getSocket(token);
    socket.emit(CLIENT_EVENTS.RPS_ROOM_LEAVE, { room_id: roomId });
  }, []);
}

/** Player bấm sẵn sàng */
export function useRpsRoomReady() {
  return useCallback((roomId: string) => {
    const token = sessionStore.getState().token;
    if (!token) return;
    const socket = getSocket(token);
    socket.emit(CLIENT_EVENTS.RPS_ROOM_READY, { room_id: roomId });
  }, []);
}

/** Player gửi lựa chọn kéo/búa/bao */
export function useRpsRoomChoose() {
  return useCallback((roomId: string, gameNumber: number, choice: 'rock' | 'paper' | 'scissors') => {
    const token = sessionStore.getState().token;
    if (!token) return;
    const socket = getSocket(token);
    socket.emit(CLIENT_EVENTS.RPS_ROOM_CHOOSE, { room_id: roomId, game_number: gameNumber, choice });
    rpsRoomStore.setState({ myChoiceSubmitted: true });
  }, []);
}

/** Gửi tin nhắn chat trong phòng (player & spectator đều dùng được) */
export function useRpsRoomChat() {
  return useCallback((roomId: string, message: string) => {
    const token = sessionStore.getState().token;
    if (!token) return;
    const socket = getSocket(token);
    socket.emit(CLIENT_EVENTS.RPS_ROOM_CHAT, { room_id: roomId, message });
  }, []);
}
